import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LockIcon from "@mui/icons-material/Lock";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import { toast } from "react-toastify";
import {
  CRYPTO_ASSETS,
  CRYPTO_NETWORKS,
  WALLET_PROVIDERS,
  PROVIDER_COLORS,
  DEFAULT_ASSET,
  DEFAULT_NETWORK,
  MAX_ACTIVE_WALLETS_PER_USER,
  generateWalletId,
  maskAddress,
  formatWalletDisplay,
  validateWalletAddress,
  WALLET_TYPES,
  getWalletCooldownUntil,
} from "../../../../Utils/cryptoWalletUtils";

const normalizeAddressForCompare = (address, network) => {
  const trimmed = (address || "").trim();
  if (!trimmed) return "";
  if (network === "POLYGON" || network === "BSC" || network === "ETHEREUM") {
    return trimmed.toLowerCase();
  }
  return trimmed;
};

// user_type: 0 = Individual, 1 = Corporate
const CLIENT_TYPES = [
  { value: null, label: "All" },
  { value: 0,    label: "Individual" },
  { value: 1,    label: "Corporate" },
];

const ASSET_COLORS = {
  USDT: { bg: "#26a17b33", text: "#2af598" },
  USDC: { bg: "#2775ca33", text: "#7eb8ff" },
  BTC:  { bg: "#f7931a33", text: "#f7931a" },
};

const TYPE_CONFIG = {
  [WALLET_TYPES.WITHDRAWAL]: {
    color: "#ff8a80",
    bg: "rgba(211,47,47,0.18)",
    border: "rgba(239,83,80,0.45)",
    // Gradient applied to dialog paper background
    paperGradient: "linear-gradient(160deg, rgba(120,10,10,0.35) 0%, rgba(18,18,40,0) 55%)",
    badgeBg: "rgba(211,47,47,0.25)",
    badgeColor: "#ff8a80",
    badgeBorder: "rgba(239,83,80,0.5)",
    label: "Withdrawal",
    description: "Client receives funds at this address when a withdrawal is processed.",
    cooldownNote: "Auto-pay to this wallet will be enabled after 24 hours.",
  },
  [WALLET_TYPES.DEPOSIT]: {
    color: "#2af598",
    bg: "rgba(42,245,152,0.15)",
    border: "rgba(42,245,152,0.35)",
    paperGradient: "linear-gradient(160deg, rgba(10,80,40,0.35) 0%, rgba(18,18,40,0) 55%)",
    badgeBg: "rgba(42,245,152,0.15)",
    badgeColor: "#2af598",
    badgeBorder: "rgba(42,245,152,0.4)",
    label: "Deposit",
    description: "Fiddux wallet address dedicated to this client. The client sends funds TO this address to deposit into Fiddux.",
    cooldownNote: "This wallet will be visible to the client after 24 hours.",
  },
};

/**
 * AddWalletModal
 * @param {string} initialWalletType - WALLET_TYPES.WITHDRAWAL | WALLET_TYPES.DEPOSIT
 * @param {object} prefill - optional { address, asset, network, label } to pre-fill the form
 *   (e.g. importing a wallet confirmed in Wallet Check). Admin still reviews and confirms.
 */
const AddWalletModal = ({ open, onClose, onSuccess, preselectedClient, initialWalletType = WALLET_TYPES.WITHDRAWAL, prefill = null }) => {
  const [allClients, setAllClients] = useState([]);
  const [clientTypeFilter, setClientTypeFilter] = useState(null);
  const [userId, setUserId] = useState("");
  const [address, setAddress] = useState("");
  const [addressError, setAddressError] = useState("");
  const [label, setLabel] = useState("");
  const [asset, setAsset] = useState(DEFAULT_ASSET);
  const [network, setNetwork] = useState(DEFAULT_NETWORK);
  const [provider, setProvider] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [skipCooldown, setSkipCooldown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const [clientWallets, setClientWallets] = useState([]);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [pendingConfirmations, setPendingConfirmations] = useState([]);

  const walletType = initialWalletType;
  const typeConf = TYPE_CONFIG[walletType] || TYPE_CONFIG[WALLET_TYPES.WITHDRAWAL];
  const isWithdrawal = walletType === WALLET_TYPES.WITHDRAWAL;

  // Load clients once on open (active accounts only; preselected client kept if inactive)
  useEffect(() => {
    if (!open) return;
    const preselectedId = preselectedClient?.doc_id || "";
    getDocs(query(collection(DATABASE, "users"), where("role", "==", 1)))
      .then((snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => {
            const id = c.doc_id || c.id;
            return c.status === 1 || (preselectedId && id === preselectedId);
          })
          .sort((a, b) =>
            (a.full_name || a.email || "").localeCompare(b.full_name || b.email || "", undefined, { sensitivity: "base" })
          );
        setAllClients(list);
      })
      .catch(() => toast.error("Failed to load clients"));
  }, [open, preselectedClient?.doc_id]);

  // Apply preselected client from URL context
  useEffect(() => {
    if (open && preselectedClient?.doc_id) setUserId(preselectedClient.doc_id);
  }, [open, preselectedClient]);

  // Apply prefilled wallet (e.g. importing from Wallet Check)
  useEffect(() => {
    if (!open || !prefill) return;
    const net = CRYPTO_NETWORKS.includes(prefill.network) ? prefill.network : DEFAULT_NETWORK;
    if (CRYPTO_ASSETS.includes(prefill.asset)) setAsset(prefill.asset);
    setNetwork(net);
    if (prefill.label) setLabel(prefill.label);
    if (prefill.address) {
      setAddress(prefill.address);
      const result = validateWalletAddress(prefill.address, net);
      setAddressError(result.valid ? "" : result.message);
    }
  }, [open, prefill]);

  // Load all wallets for selected client (active + inactive)
  useEffect(() => {
    if (!open || !userId) {
      setActiveCount(0);
      setClientWallets([]);
      return;
    }
    setLoadingWallets(true);
    getDocs(collection(DATABASE, "users", userId, "crypto_wallets"))
      .then((snap) => {
        const wallets = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            if (a.is_default && !b.is_default) return -1;
            if (!a.is_default && b.is_default) return 1;
            const aActive = a.is_active !== false;
            const bActive = b.is_active !== false;
            if (aActive && !bActive) return -1;
            if (!aActive && bActive) return 1;
            return String(a.id || "").localeCompare(String(b.id || ""));
          });
        const active = wallets.filter((w) => w.is_active !== false).length;
        setClientWallets(wallets);
        setActiveCount(active);
        setIsDefault(active === 0);
      })
      .catch(() => {
        toast.error("Failed to load client wallets");
        setClientWallets([]);
        setActiveCount(0);
      })
      .finally(() => setLoadingWallets(false));
  }, [open, userId]);

  // Load wallets saved for review in Wallet Check (new wallets pending admin add)
  useEffect(() => {
    if (!open || !userId) {
      setPendingConfirmations([]);
      return;
    }
    getDocs(collection(DATABASE, "users", userId, "wallet_confirmations"))
      .then((snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => c.address && c.is_new_wallet !== false)
          .sort((a, b) => (b.confirmed_at?.seconds || 0) - (a.confirmed_at?.seconds || 0));
        setPendingConfirmations(list.slice(0, 8));
      })
      .catch(() => setPendingConfirmations([]));
  }, [open, userId]);

  const applyConfirmation = (c) => {
    const net = CRYPTO_NETWORKS.includes(c.network) ? c.network : DEFAULT_NETWORK;
    if (CRYPTO_ASSETS.includes(c.asset)) setAsset(c.asset);
    setNetwork(net);
    setAddress(c.address);
    validateAddr(c.address, net);
  };

  const reviewableConfirmations = useMemo(() => {
    if (pendingConfirmations.length === 0) return [];
    const registeredNorm = new Set(
      clientWallets.map((w) => normalizeAddressForCompare(w.address, w.network || DEFAULT_NETWORK))
    );
    return pendingConfirmations.filter(
      (c) => !registeredNorm.has(normalizeAddressForCompare(c.address, c.network || DEFAULT_NETWORK))
    );
  }, [pendingConfirmations, clientWallets]);

  const filteredClients = useMemo(() => {
    if (clientTypeFilter === null) return allClients;
    return allClients.filter((c) => c.user_type === clientTypeFilter);
  }, [allClients, clientTypeFilter]);

  const validateAddr = (val, net) => {
    const result = validateWalletAddress(val, net);
    setAddressError(result.valid ? "" : result.message);
    return result.valid;
  };

  const resetForm = () => {
    setAddress(""); setAddressError(""); setLabel("");
    setAsset(DEFAULT_ASSET); setNetwork(DEFAULT_NETWORK); setProvider("");
    setIsDefault(true); setSkipCooldown(false); setClientTypeFilter(null);
    setClientWallets([]);
    setPendingConfirmations([]);
    setActiveCount(0);
    if (!preselectedClient?.doc_id) setUserId("");
  };

  const handleClose = () => { resetForm(); onClose(); };

  const handleSubmit = async () => {
    if (!userId) { toast.error("Select a client"); return; }
    if (isWithdrawal && activeCount >= MAX_ACTIVE_WALLETS_PER_USER) {
      toast.error(`Maximum ${MAX_ACTIVE_WALLETS_PER_USER} active wallets per client`);
      return;
    }
    if (!validateAddr(address, network)) return;
    if (duplicateWallet) {
      toast.error("This asset/network/address is already registered for this client");
      return;
    }

    setSaving(true);
    try {
      const existingSnap = await getDocs(collection(DATABASE, "users", userId, "crypto_wallets"));
      const existing = existingSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const walletId = generateWalletId(existing);
      const adminEmail = localStorage.getItem("email") || "admin";
      const batch = writeBatch(DATABASE);

      if (isWithdrawal && isDefault) {
        existing.filter((w) => w.is_active && w.is_default).forEach((w) => {
          batch.update(doc(DATABASE, "users", userId, "crypto_wallets", w.id), { is_default: false });
        });
      }

      const eligibleFrom = skipCooldown
        ? Timestamp.fromDate(new Date(Date.now() - 1000))
        : Timestamp.fromDate(getWalletCooldownUntil());

      batch.set(doc(DATABASE, "users", userId, "crypto_wallets", walletId), {
        id: walletId,
        address: address.trim(),
        label: label.trim() || `${asset} ${typeConf.label} Wallet`,
        asset,
        network,
        provider: provider || "",
        wallet_type: walletType,
        is_active: true,
        is_default: isWithdrawal ? (isDefault || activeCount === 0) : false,
        added_date: serverTimestamp(),
        added_by: adminEmail,
        auto_pay_eligible_from: eligibleFrom,
        deactivated_date: null,
        deactivated_by: null,
        edit_history: [],
      });

      await batch.commit();
      toast.success(
        skipCooldown
          ? `${typeConf.label} wallet added — cooldown bypassed (admin override)`
          : `${typeConf.label} wallet added — auto-pay eligible in 24h`
      );
      resetForm();
      onSuccess?.();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error("Failed to add wallet");
    } finally {
      setSaving(false);
    }
  };

  const selectedClient = allClients.find((c) => (c.doc_id || c.id) === userId);
  const addressPreview = address.trim() ? maskAddress(address.trim(), network) : null;
  const atLimit = isWithdrawal && activeCount >= MAX_ACTIVE_WALLETS_PER_USER;
  const inactiveCount = clientWallets.filter((w) => w.is_active === false).length;

  const duplicateWallet = useMemo(() => {
    if (!address.trim() || clientWallets.length === 0) return null;
    const incoming = normalizeAddressForCompare(address, network);
    const incomingAsset = String(asset || DEFAULT_ASSET).toUpperCase();
    const incomingNet = network || DEFAULT_NETWORK;
    return (
      clientWallets.find((w) => {
        const existingNet = w.network || DEFAULT_NETWORK;
        const existingAsset = String(w.asset || DEFAULT_ASSET).toUpperCase();
        const existingType = w.wallet_type || WALLET_TYPES.WITHDRAWAL;
        if (existingNet !== incomingNet) return false;
        if (existingAsset !== incomingAsset) return false;
        if (existingType !== walletType) return false;
        return normalizeAddressForCompare(w.address, existingNet) === incoming;
      }) || null
    );
  }, [address, network, asset, clientWallets, walletType]);

  const duplicateMessage = duplicateWallet
    ? `Already registered: ${formatWalletDisplay(duplicateWallet)}${duplicateWallet.is_active === false ? " (inactive)" : ""}`
    : "";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#1a1a2e",
          backgroundImage: typeConf.paperGradient,
          backgroundRepeat: "no-repeat",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              px: 1.5, py: 0.5, borderRadius: 1,
              bgcolor: typeConf.badgeBg,
              border: `1px solid ${typeConf.badgeBorder}`,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, color: typeConf.badgeColor, letterSpacing: 1, textTransform: "uppercase" }}>
              {typeConf.label}
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "text.primary", lineHeight: 1.2 }}>
              Add {typeConf.label} Wallet
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {typeConf.description}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

          {/* ── Security banner ── */}
          <Box sx={{
            display: "flex", alignItems: "flex-start", gap: 1.5, p: 1.5, borderRadius: 1,
            bgcolor: "rgba(255,193,7,0.1)", border: "1px solid rgba(255,193,7,0.3)",
          }}>
            <AccessTimeIcon sx={{ fontSize: 18, color: "#ffd54f", mt: 0.25, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" sx={{ color: "#ffd54f", fontWeight: 700, fontSize: 13 }}>
                24-hour security cooldown
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", fontSize: 12, lineHeight: 1.5, mt: 0.25 }}>
                {typeConf.cooldownNote} An email notification will be sent to all admins when this wallet is registered.
              </Typography>
            </Box>
          </Box>

          {/* ── Admin: skip cooldown ── */}
          {isWithdrawal && (
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              px: 1.5, py: 1, borderRadius: 1,
              bgcolor: skipCooldown ? "rgba(244,67,54,0.12)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${skipCooldown ? "rgba(244,67,54,0.45)" : "rgba(255,255,255,0.1)"}`,
            }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12, color: skipCooldown ? "#ff8a80" : "rgba(255,255,255,0.7)" }}>
                  Admin override — skip 24h cooldown
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", fontSize: 11 }}>
                  Auto-pay eligible immediately. Use only for testing.
                </Typography>
              </Box>
              <Switch
                size="small"
                checked={skipCooldown}
                onChange={(e) => setSkipCooldown(e.target.checked)}
                sx={{ "& .MuiSwitch-thumb": { bgcolor: skipCooldown ? "#ff8a80" : undefined } }}
              />
            </Box>
          )}

          {/* ── Address immutability note ── */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, borderRadius: 1,
            bgcolor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
          }}>
            <LockIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.5)", flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)", fontSize: 12 }}>
              Wallet address cannot be changed after registration.
            </Typography>
          </Box>

          {/* ── Client selection ── */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}>
              Client
            </Typography>

            {!preselectedClient?.doc_id && (
              <ToggleButtonGroup
                value={clientTypeFilter}
                exclusive
                onChange={(_, v) => { setClientTypeFilter(v); setUserId(""); }}
                size="small"
                sx={{ mb: 1 }}
              >
                {CLIENT_TYPES.map((ct) => (
                  <ToggleButton
                    key={String(ct.value)}
                    value={ct.value}
                    sx={{ px: 2, textTransform: "none", color: "text.secondary", "&.Mui-selected": { color: "text.primary" } }}
                  >
                    {ct.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}

            <TextField
              select
              label="Select client"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              fullWidth
              size="small"
              disabled={!!preselectedClient?.doc_id}
              InputLabelProps={{ sx: { color: "text.secondary" } }}
              sx={{ "& .MuiInputBase-input": { color: "text.primary" } }}
              helperText={
                userId && !atLimit
                  ? `${activeCount}/${MAX_ACTIVE_WALLETS_PER_USER} active wallets`
                  : userId && atLimit
                  ? "⚠ Maximum active wallets reached for this client"
                  : null
              }
              FormHelperTextProps={{ sx: { color: atLimit ? "error.main" : "text.secondary" } }}
            >
              <MenuItem value=""><em>Select…</em></MenuItem>
              {filteredClients.map((c) => {
                const id = c.doc_id || c.id;
                return (
                  <MenuItem key={id} value={id}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 600 }}>
                        {c.full_name || c.email}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>{c.email}</Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </TextField>

            {selectedClient && (
              <Box sx={{ display: "flex", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
                <Chip
                  size="small"
                  label={selectedClient.user_type === 1 ? "Corporate" : "Individual"}
                  variant="outlined"
                  color={selectedClient.user_type === 1 ? "warning" : "info"}
                />
                {atLimit && (
                  <Chip size="small" label="At wallet limit" color="error" variant="filled" />
                )}
              </Box>
            )}

            {/* Existing wallets panel */}
            {userId && (
              <Box sx={{
                mt: 1.5, p: 1.5, borderRadius: 1, border: "1px solid",
                borderColor: clientWallets.length > 0 ? "rgba(255,193,7,0.3)" : "rgba(255,255,255,0.12)",
                bgcolor: "rgba(0,0,0,0.2)",
              }}>
                <Typography variant="caption" sx={{ display: "block", mb: 1, textTransform: "uppercase", letterSpacing: 0.6, color: "rgba(255,255,255,0.5)", fontWeight: 600 }}>
                  Registered wallets for this client
                </Typography>

                {loadingWallets ? (
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>Loading…</Typography>
                ) : clientWallets.length === 0 ? (
                  <Alert severity="success" variant="outlined" sx={{ py: 0.5 }}>
                    No wallets registered yet — you can add up to {MAX_ACTIVE_WALLETS_PER_USER} active wallets.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ mb: 1, color: "rgba(255,255,255,0.8)" }}>
                      <strong style={{ color: "#fff" }}>{activeCount}</strong> active
                      {inactiveCount > 0 ? <> · <strong style={{ color: "#fff" }}>{inactiveCount}</strong> inactive</> : null}
                      {" · "}max <strong style={{ color: "#fff" }}>{MAX_ACTIVE_WALLETS_PER_USER}</strong> active — duplicate = same type + asset + network + address (EVM: same 0x can be used for USDT and USDC)
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                      {clientWallets.map((wallet) => {
                        const isActive = wallet.is_active !== false;
                        const walletNet = wallet.network || DEFAULT_NETWORK;
                        const wType = wallet.wallet_type || WALLET_TYPES.WITHDRAWAL;
                        const wTypeConf = TYPE_CONFIG[wType] || TYPE_CONFIG[WALLET_TYPES.WITHDRAWAL];
                        return (
                          <Box key={wallet.id} sx={{
                            p: 1, borderRadius: 1,
                            border: "1px solid rgba(255,255,255,0.1)",
                            bgcolor: isActive ? "rgba(42,245,152,0.05)" : "rgba(255,255,255,0.02)",
                            opacity: isActive ? 1 : 0.65,
                          }}>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
                              <Chip size="small" label={wTypeConf.label} sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: wTypeConf.bg, color: wTypeConf.color, border: `1px solid ${wTypeConf.border}` }} />
                              <Chip size="small" label={wallet.asset || DEFAULT_ASSET} sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: ASSET_COLORS[wallet.asset]?.bg || "rgba(255,255,255,0.08)", color: ASSET_COLORS[wallet.asset]?.text || "#fff" }} />
                              <Chip size="small" label={walletNet} variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                              {wallet.is_default && isActive && <Chip size="small" label="Default" color="success" sx={{ height: 20, fontSize: 10 }} />}
                              <Chip size="small" label={isActive ? "Active" : "Inactive"} color={isActive ? "success" : "default"} variant={isActive ? "filled" : "outlined"} sx={{ height: 20, fontSize: 10 }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", fontSize: 12 }}>
                              {wallet.label || `${wallet.asset || DEFAULT_ASSET} Wallet`}
                            </Typography>
                            <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                              {maskAddress(wallet.address, walletNet)}
                              {wallet.address ? ` · ends …${wallet.address.slice(-4)}` : ""}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* ── Pending wallet confirmations from Wallet Check ── */}
          {isWithdrawal && userId && reviewableConfirmations.length > 0 && (
            <Box sx={{
              p: 1.5, borderRadius: 1,
              border: "1px solid rgba(126,184,255,0.35)",
              bgcolor: "rgba(126,184,255,0.06)",
            }}>
              <Typography variant="caption" sx={{ display: "block", mb: 0.5, textTransform: "uppercase", letterSpacing: 0.6, color: "#7eb8ff", fontWeight: 700 }}>
                Saved in Wallet Check — pending review
              </Typography>
              <Typography variant="caption" sx={{ display: "block", mb: 1, color: "rgba(255,255,255,0.6)" }}>
                Addresses your team confirmed for this client. Tap "Use" to prefill, review, then add.
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {reviewableConfirmations.map((c) => (
                  <Box key={c.id} sx={{
                    p: 1, borderRadius: 1,
                    border: "1px solid rgba(255,255,255,0.1)",
                    bgcolor: "rgba(0,0,0,0.2)",
                    display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap",
                  }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: "flex", gap: 0.5, mb: 0.25, flexWrap: "wrap", alignItems: "center" }}>
                        <Chip size="small" label={c.asset || DEFAULT_ASSET} sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: ASSET_COLORS[c.asset]?.bg || "rgba(255,255,255,0.08)", color: ASSET_COLORS[c.asset]?.text || "#fff" }} />
                        <Chip size="small" label={c.network || DEFAULT_NETWORK} variant="outlined" sx={{ height: 18, fontSize: 9 }} />
                        {c.photo_url && (
                          <Box component="a" href={c.photo_url} target="_blank" rel="noreferrer" sx={{ color: "#7eb8ff", fontSize: 10, textDecoration: "none" }}>
                            photo
                          </Box>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary", wordBreak: "break-all" }}>
                        {maskAddress(c.address, c.network || DEFAULT_NETWORK)}{c.address ? ` · …${c.address.slice(-4)}` : ""}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => applyConfirmation(c)}
                      sx={{ flexShrink: 0, color: "#7eb8ff", borderColor: "rgba(126,184,255,0.4)", fontWeight: 700, "&:hover": { borderColor: "#7eb8ff", bgcolor: "rgba(126,184,255,0.08)" } }}
                    >
                      Use
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* ── Asset + Network ── */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}>
              Asset & Network
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <TextField
                select label="Asset" value={asset}
                onChange={(e) => setAsset(e.target.value)}
                size="small" sx={{ flex: 1 }}
                InputLabelProps={{ sx: { color: "text.secondary" } }}
                SelectProps={{ sx: { color: "text.primary" } }}
              >
                {CRYPTO_ASSETS.map((a) => (
                  <MenuItem key={a} value={a}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: ASSET_COLORS[a]?.text || "grey" }} />
                      <Typography variant="body2" sx={{ color: "text.primary" }}>{a}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select label="Network" value={network}
                onChange={(e) => { setNetwork(e.target.value); if (address) validateAddr(address, e.target.value); }}
                size="small" sx={{ flex: 1.4 }}
                InputLabelProps={{ sx: { color: "text.secondary" } }}
                SelectProps={{ sx: { color: "text.primary" } }}
              >
                {CRYPTO_NETWORKS.map((n) => (
                  <MenuItem key={n} value={n}>
                    <Typography variant="body2" sx={{ color: "text.primary" }}>{n}</Typography>
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <TextField
              select
              label="Provider"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              size="small"
              fullWidth
              sx={{ mt: 1.5 }}
              helperText="Exchange or app that holds this address. Optional."
              InputLabelProps={{ sx: { color: "text.secondary" } }}
              SelectProps={{ sx: { color: "text.primary" } }}
            >
              <MenuItem value="">
                <Typography variant="body2" sx={{ color: "text.primary" }}>Not set</Typography>
              </MenuItem>
              {WALLET_PROVIDERS.map((name) => (
                <MenuItem key={name} value={name}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: PROVIDER_COLORS[name]?.bg || "grey" }} />
                    <Typography variant="body2" sx={{ color: "text.primary" }}>{name}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* ── Wallet address ── */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}>
              Wallet Address <Typography component="span" sx={{ color: "error.main" }}>*</Typography>
            </Typography>
            <TextField
              label="Address"
              value={address}
              onChange={(e) => { setAddress(e.target.value); if (e.target.value) validateAddr(e.target.value, network); else setAddressError(""); }}
              onBlur={(e) => { if (e.target.value) validateAddr(e.target.value, network); }}
              fullWidth
              size="small"
              required
              error={!!addressError || !!duplicateWallet}
              helperText={
                duplicateMessage || addressError ||
                (network === "POLYGON" || network === "BSC" || network === "ETHEREUM"
                  ? "EVM address: 0x + 40 hex characters"
                  : network === "TRON" ? "TRON address: T + 33 characters"
                  : network === "BITCOIN" ? "Bitcoin address: bech32 (bc1…) or legacy"
                  : null)
              }
              placeholder={network === "TRON" ? "T…" : network === "BITCOIN" ? "bc1…" : "0x…"}
              InputLabelProps={{ sx: { color: "rgba(255,255,255,0.6)" } }}
              FormHelperTextProps={{ sx: { color: (addressError || duplicateWallet) ? undefined : "rgba(255,255,255,0.55)" } }}
              InputProps={{
                sx: { fontFamily: "monospace", fontSize: 13, color: "#fff" },
                endAdornment: address ? (
                  <InputAdornment position="end">
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => { navigator.clipboard.writeText(address); toast.info("Address copied"); }}>
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
            />
            {addressPreview && !addressError && !duplicateWallet && (
              <Typography variant="caption" sx={{ mt: 0.5, display: "block", fontFamily: "monospace", color: "rgba(255,255,255,0.55)" }}>
                Preview: {addressPreview}
              </Typography>
            )}
          </Box>

          {/* ── Label ── */}
          <TextField
            label="Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            size="small"
            placeholder={`e.g. ${isWithdrawal ? "Personal USDT, Company Wallet" : "Bitget Sub-account #1, Bybit USDT"}`}
            helperText={
              isWithdrawal
                ? "Shown to the client when selecting a wallet in the app, and in admin Withdrawals (with last 4 address digits)"
                : "Internal label to identify this Fiddux-owned deposit address assigned to the client"
            }
            InputLabelProps={{ sx: { color: "rgba(255,255,255,0.6)" } }}
            InputProps={{ sx: { color: "#fff" } }}
            FormHelperTextProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
          />

          {/* ── Default toggle — withdrawal only ── */}
          {isWithdrawal && (
            <FormControlLabel
              control={
                <Switch
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  color="success"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: "#fff", fontWeight: 500 }}>Set as default wallet</Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)" }}>
                    Default wallet is pre-selected in the client app during withdrawal
                  </Typography>
                </Box>
              }
            />
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || atLimit || !userId || !!duplicateWallet || loadingWallets}
          sx={{
            background: isWithdrawal
              ? "linear-gradient(135deg, #c62828 0%, #ef5350 100%)"
              : "linear-gradient(135deg, #00796b 0%, #2af598 100%)",
            color: "#fff",
            fontWeight: 700,
            "&:hover": {
              background: isWithdrawal
                ? "linear-gradient(135deg, #e53935 0%, #ff6659 100%)"
                : "linear-gradient(135deg, #00897b 0%, #3adecc 100%)",
            },
            "&:disabled": { background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.3)" },
          }}
        >
          {saving ? "Saving…" : `Add ${typeConf.label} Wallet`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddWalletModal;
